/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react';
import {
    StripeCardCvcElementOptions,
    StripeCardExpiryElementOptions,
    StripeCardNumberElementOptions,
    PaymentIntent,
    StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';
import { useIntl } from 'react-intl';
import { Z_INDEX } from '../../../../constants/zIndex';
import {
    CardCvcElement,
    CardExpiryElement,
    CardNumberElement,
    useElements,
    useStripe,
    ExpressCheckoutElement,
} from '@stripe/react-stripe-js';

import { Button } from '../../../../components/button/button.component';
import payStyles from '../../pay.styles';

import stripePaymentStyles from './stripe-payment-form.styles';
import moduleStyles from './stripe-payment-form.module.css';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

type Props = {
    paymentIntent: PaymentIntent;
    onSuccess: (id: string) => void;
    guestId: string;
    priceTotal?: string;
};

const StripePaymentForm = (props: Props) => {
    const intl = useIntl();
    const stripe = useStripe();
    const navigate = useNavigate();
    const elements = useElements();
    const [display, setDisplay] = useState<'none' | 'block'>('none');
    const [isLoading, setIsLoading] = useState(false);
    const [validationState, setValidationState] = useState({
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false,
    });
    const [cardNumberError, setCardNumberError] = useState<string | null>(null);
    const [cardExpiryError, setCardExpiryError] = useState<string | null>(null);
    const [cardCvcError, setCardCvcError] = useState<string | null>(null);

    const elementOptions:
        | StripeCardNumberElementOptions
        | StripeCardExpiryElementOptions
        | StripeCardCvcElementOptions = useMemo(() => {
        return {
            style: {
                base: {
                    fontFamily: 'Inter, sans-serif',
                    color: '#000000',
                    padding: '10px 12px',
                    '::placeholder': {
                        color: '#0050c780',
                    },
                },
            },
        };
    }, []);

    const onPayClick = async () => {
        if (!stripe || !elements || !props?.paymentIntent?.client_secret) {
            return;
        }

        const cardElement = elements.getElement(CardNumberElement);

        if (!cardElement) {
            return;
        }

        setIsLoading(true);
        const { error, paymentIntent } = await stripe.confirmCardPayment(
            props.paymentIntent.client_secret as string,
            {
                payment_method: {
                    card: cardElement,
                },
            }
        );

        if (error) {
            console.error(error);
            toast.error(error.message ?? 'Something went wrong', {
                style: {
                    zIndex: Z_INDEX.TOAST,
                },
            });
        } else if (paymentIntent) {
            props.onSuccess(paymentIntent.id);
        }
        setIsLoading(false);
    };

    const opExpressPaymentConfirm = async () => {
        if (!stripe || !elements || !props.paymentIntent?.client_secret) {
            return;
        }

        const { error } = await stripe?.confirmPayment({
            elements,
            clientSecret: props.paymentIntent.client_secret,
            confirmParams: {
                return_url: `${window.location.origin}/charge/${props.guestId}`,
            },
        });

        if (error) {
            console.log('expressCheckoutError', error);
        }
    };

    const onReady = ({
        availablePaymentMethods,
        elementType,
    }: StripeExpressCheckoutElementReadyEvent) => {
        if (!availablePaymentMethods) {
            // No buttons will show
        } else if (availablePaymentMethods.applePay || availablePaymentMethods.googlePay) {
            setDisplay('block');
        }
    };

    const handleCardNumberChange = (event: any) => {
        setValidationState(prev => ({
            ...prev,
            cardNumber: event.complete,
        }));
        setCardNumberError(event.error?.message ?? null);
    };

    const handleCardExpiryChange = (event: any) => {
        setValidationState(prev => ({
            ...prev,
            cardExpiry: event.complete,
        }));
        setCardExpiryError(event.error?.message ?? null);
    };

    const handleCardCvcChange = (event: any) => {
        setValidationState(prev => ({
            ...prev,
            cardCvc: event.complete,
        }));
        setCardCvcError(event.error?.message ?? null);
    };

    const isFormValid =
        validationState.cardNumber && validationState.cardExpiry && validationState.cardCvc;

    return (
        <div css={stripePaymentStyles.outterBox}>
            <div>
                <div>
                    <div style={{ display, marginBottom: '20px' }}>
                        <ExpressCheckoutElement
                            className="stripe-btns"
                            onConfirm={opExpressPaymentConfirm}
                            onReady={onReady}
                            options={{
                                paymentMethods: {
                                    applePay: 'always',
                                    googlePay: 'always',
                                    amazonPay: 'never',
                                    link: 'never',
                                    paypal: 'never',
                                },
                            }}
                        />
                    </div>
                    <div css={stripePaymentStyles.orUseCardBlock} style={{ display }}>
                        <div css={stripePaymentStyles.orUseCardContainer}>
                            <div css={stripePaymentStyles.borderLine}></div>
                            <span css={stripePaymentStyles.orUseCardText}>
                                {intl.formatMessage({ id: 'orUseCard' })}
                            </span>
                            <div css={stripePaymentStyles.borderLine}></div>
                        </div>
                    </div>
                    <div css={stripePaymentStyles.cardNumberRow}>
                        <span css={stripePaymentStyles.formLabel}>
                            {intl.formatMessage({ id: 'stripe.cardNumber' })}
                        </span>
                        <CardNumberElement
                            className={moduleStyles.input}
                            options={elementOptions}
                            onChange={handleCardNumberChange}
                        />
                        {cardNumberError && (
                            <span css={stripePaymentStyles.error}>{cardNumberError}</span>
                        )}
                    </div>
                    <div css={stripePaymentStyles.row}>
                        <div css={stripePaymentStyles.halfRow}>
                            <span css={stripePaymentStyles.formLabel}>
                                {intl.formatMessage({ id: 'stripe.expiriationDate' })}
                            </span>
                            <CardExpiryElement
                                className={moduleStyles.input}
                                options={elementOptions}
                                onChange={handleCardExpiryChange}
                            />
                            {cardExpiryError && (
                                <span css={stripePaymentStyles.error}>{cardExpiryError}</span>
                            )}
                        </div>
                        <div css={stripePaymentStyles.halfRow}>
                            <span css={stripePaymentStyles.formLabel}>CVS/CVV</span>
                            <CardCvcElement
                                className={moduleStyles.input}
                                options={{ ...elementOptions, placeholder: '123' }}
                                onChange={handleCardCvcChange}
                            />
                            {cardCvcError && (
                                <span css={stripePaymentStyles.error}>{cardCvcError}</span>
                            )}
                        </div>
                    </div>
                    <div css={payStyles.actionWrapper}>
                        <Button
                            text={intl.formatMessage(
                                { id: 'payAmmount' },
                                { amount: props.priceTotal }
                            )}
                            onClick={onPayClick}
                            isLoading={isLoading}
                            disabled={!isFormValid}
                        />
                        <Button
                            styles={stripePaymentStyles.backBtnStyles}
                            type={'link'}
                            text={intl.formatMessage({ id: 'back' })}
                            onClick={() => navigate(-1)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StripePaymentForm;
